/**
 * Demo Finish Step — account creation + demo confirmation for un-authed visitors.
 *
 * The demo flow's whole endgame: `CompleteAccountForm` in `create` mode
 * registers a full account in ONE shot (`POST /supabase-auth/register` —
 * active status, home enrollment, live session; never an incomplete user),
 * then waivers post, then the demo booking is created. Mirrors
 * `FinishAccountStep`'s layout (details card left, form + actions right,
 * external `form=` submit) minus everything payment.
 *
 * Waiver ordering is deliberate and mirrors `FinishAccountStep`: signatures
 * were captured earlier (store-only) and post FIRST after the account exists —
 * registering flips `isAuthenticated`, which swaps the requirements query
 * underneath us, so we submit exactly what was captured rather than re-reading.
 * A missing signature aborts before any booking is created.
 *
 * Email branches: an email that already has an account (409 EMAIL_IN_USE_*)
 * opens the shared sign-in modal — on success the visitor is authed with their
 * selections intact and confirms from the time step; the modal's existing
 * ACCOUNT_SETUP_INCOMPLETE handling routes incomplete accounts to
 * /account-setup. An other-tenant email renders inline (form-owned copy).
 *
 * @module pages/demo/steps/DemoFinishStep
 */

import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { BookingDetailsCard } from '@butter/ui/domains/booking';
import { addBreadcrumb, captureException } from '@butter/monitoring';
import { createClubDateTimeFromSlot, formatTime, timeStringToMinutes } from '@butter/library/datetime';
import { useBooking, useCreateDemoBooking } from '~/services/internalApi/booking';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useHashModal } from '~/hooks/useHashModal';
import { setSignInPrompt } from '~/ui/layout/SignInModal/signInPrompt';
import { useAuth } from '~/services/auth';
import { toNumericUserId } from '~/lib/utils/userIdUtils';
import { useTenantNavigate } from '~/lib/navigation';
import { formatDuration } from '../../booking/shared/formatDuration';
import { CompleteAccountForm } from '../../booking/components/CompleteAccountForm';
import { WaiverSection } from '../../booking/components/WaiverSection';
import { useWaiverSubmission } from '../../booking/components/useWaiverSubmission';
import { waiverRefusalMessage } from '../../booking/components/waiverCompletion';
import { useWaiverGateHold } from '../../../features/waivers/waiverGateHold';
import { useVisualStep, VISUAL_STEPS } from '~/services/internalApi/booking/context';

export interface DemoFinishStepProps {
  onBack: () => void;
}

export function DemoFinishStep({ onBack }: DemoFinishStepProps) {
  const navigateWithParams = useTenantNavigate();
  const signInModal = useHashModal('dialog-sign-in');
  const { isAuthenticated } = useAuth();
  const { setVisualStep } = useVisualStep();

  const { data: club } = useClubQuery();
  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    availableBays,
    clearBooking,
  } = useBooking();

  const waiverGate = useWaiverSubmission(club?.id);
  // Registering flips auth mid-step; hold the entry gate so it can't swap the
  // flow for a SignWaiversScreen while this step posts the captured consent.
  useWaiverGateHold();

  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectedBay = availableBays?.items.find((b) => b.id === selectedBayId);
  // Null-guarded, not `?? ''`: timeStringToMinutes THROWS on an empty string,
  // and this step renders once more with a cleared context after the booking
  // is created (clearBooking → re-render → navigation unmounts) — that render
  // must not throw, or React recovers with a "display error" toast.
  const startMinutes = selectedStartTime ? timeStringToMinutes(selectedStartTime) : null;
  const endMinutes = selectedEndTime ? timeStringToMinutes(selectedEndTime) : null;
  const durationMinutes = (endMinutes || 0) - (startMinutes || 0);

  const createDemoBooking = useCreateDemoBooking({
    onSuccess: (result) => {
      const { booking, accessControl, access } = result;
      addBreadcrumb('booking', 'Demo booking created (new account)', {
        bookingId: booking.id,
      }, 'info');

      clearBooking();

      // useTenantNavigate carries tenant-handle + club-id automatically.
      navigateWithParams(`/demo/confirmed/${booking.id}`, {
        state: { booking, accessControl, access },
      });
    },
    onError: (err) => {
      // The account exists and the session is live — only the booking failed
      // (e.g. the slot was taken while they typed). Keep them here to re-pick.
      setError(err.message || 'We couldn’t book that demo time. Please go back and choose another slot.');
      captureException(err, {
        level: 'error',
        tags: { operation: 'create_demo_booking', stage: 'post_register' },
        extra: { selectedBayId, selectedDate, selectedStartTime, selectedEndTime },
      });
      setIsProcessing(false);
    },
  });

  /** Fired by CompleteAccountForm AFTER register() succeeded (session live). */
  const handleAccountComplete = async (user: { id: string | number }) => {
    if (!club || !selectedBayId || !club.timezone) {
      setError('Your selections are incomplete. Please go back and pick a time and bay.');
      return;
    }
    try {
      setIsProcessing(true);
      setError(null);

      // Waivers FIRST — captured on this step, posted before anything is booked.
      // Reads the pre-flip answer; see useWaiverSubmission for why.
      if (!waiverGate.satisfied) {
        throw new Error(waiverRefusalMessage(waiverGate.answerMissing));
      }
      await waiverGate.submitWaivers();

      // Rebuild the UTC instants here rather than threading them from the time
      // step — the conversion is deterministic from context state.
      const startISO = createClubDateTimeFromSlot(selectedDate, selectedStartTime!, club.timezone).date.toISOString();
      const endISO = createClubDateTimeFromSlot(selectedDate, selectedEndTime!, club.timezone).date.toISOString();

      createDemoBooking.mutate({
        clubId: club.id,
        bayId: selectedBayId,
        userId: toNumericUserId(user.id),
        startTime: startISO,
        endTime: endISO,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  /**
   * The entered email already has an account (either 409 from register). Hand
   * off to the shared sign-in modal with context + the email prefilled; the
   * bounce-back effect below returns them to their selections once signed in.
   */
  const awaitingSignIn = useRef(false);
  const handleAccountExists = (email: string) => {
    setSignInPrompt({
      message: 'An account with this email already exists — sign in to book your demo.',
      email,
    });
    awaitingSignIn.current = true;
    signInModal.open();
  };

  // Bounce back to the selection step once the sign-in the handoff asked for
  // succeeds: selections live in the in-memory provider (the modal is an
  // overlay, nothing unmounted), so the member lands on their chosen time and
  // bay with the CTA already reading "Confirm Demo Booking".
  //
  // Gated on the ref, NOT bare isAuthenticated — the normal register path also
  // flips auth mid-step, and bouncing then would unmount this step under its
  // own in-flight create. Register never sets the ref.
  useEffect(() => {
    if (!awaitingSignIn.current) return;
    if (isAuthenticated) {
      awaitingSignIn.current = false;
      onBack();
    } else if (!signInModal.isOpen) {
      // Modal dismissed without signing in — a later register must not bounce.
      awaitingSignIn.current = false;
    }
  }, [isAuthenticated, signInModal.isOpen, onBack]);

  const handleBack = () => {
    setVisualStep(VISUAL_STEPS.BAY_SELECTION);
    onBack();
  };

  return (
    <div className="">
      {error && (
        <div
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="polite"
          data-testid="confirmation-error-message"
        >
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Two Column Layout - Desktop | Single Column - Mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN — demo summary. No price card: a demo is free. */}
        <div className="space-y-6">
          {club && selectedDate && selectedStartTime && selectedEndTime && (
            <BookingDetailsCard
              imageUrl={selectedBay?.image?.url || null}
              imageAlt={selectedBay?.image?.alt}
              name={selectedBay?.name || 'Bay'}
              bayId={selectedBayId || 0}
              date={format(parseISO(selectedDate), 'EEEE, d MMMM, yyyy')}
              time={`${formatTime(selectedStartTime)} - ${formatTime(selectedEndTime)}`}
              duration={formatDuration(durationMinutes)}
            />
          )}
        </div>

        {/* RIGHT COLUMN — waivers + account form + actions */}
        <div className="space-y-6 p-4">
          {club && <WaiverSection clubId={club.id} disabled={isProcessing} />}

          {club && (
            <CompleteAccountForm
              mode="create"
              clubId={club.id}
              phoneRegion={club.country}
              onComplete={handleAccountComplete}
              onAccountExists={handleAccountExists}
            />
          )}

          <div className="flex flex-col text-sm sm:flex-row gap-3">
            <button
              onClick={handleBack}
              disabled={isProcessing}
              data-testid="confirmation-back-button"
              className="flex-1 px-6 py-2 bg-white text-gray-700 border border-theme-600 rounded-full font-medium hover:bg-gray-50 focus:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>

            <button
              type="submit"
              form="complete-account-form"
              // Blocking here rather than only in the handler: this form REGISTERS
              // the account before `onComplete` runs, so a refusal after the click
              // leaves an account with no booking. Safe to disable now that a failed
              // requirements query renders WaiverSection's retry banner above —
              // before, this would have been a dead button with no explanation.
              disabled={isProcessing || !waiverGate.satisfied}
              data-testid="confirmation-form-submit-button"
              className="flex-1 px-6 py-2 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing...' : 'Save and Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
